import React, { useState, useEffect, useRef, useContext } from "react";
import NavSub from "../components/NavSub";
import "../Styles/SynergyBillMonth.css";
import { FileArrowUp, FunnelSimple, CaretDown, CaretUp } from "phosphor-react";
import { AuthContext } from "../context/AuthContext";
import { reportApiUrl } from "../utils/GetUrl";
import * as XLSX from "xlsx";
import { CircularProgress } from "@mui/material";

function SynergyBillMonth() {
  const currentYear = new Date().getFullYear(); // Get the current year
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const { token, logout, handleAuthExpiry } = useContext(AuthContext);
  const toastRef = useRef();
  const [loading, setLoading] = useState(false);
  const [end, setEnd] = useState(false);
  const [customerCount, setCustomerCount] = useState(null);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const dropdownRef = useRef(null);

  const handleClickOutside = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setIsDropdownOpen(false);
    }
  };

  // Add event listener for outside clicks
  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Handle option click and update the selected option

  //integration
  const [monthlyBill, setMonthlyBill] = useState({});

  useEffect(() => {
    if (!token) return;
    const fetchMonthlyBillData = async (selectedYear) => {
      if (!selectedYear) return; // Check if selectedMonth is available

      const year = selectedYear;
      setLoading(true);

      try {
        const response = await fetch(
          `${reportApiUrl}/GetBillingByDepartment?year=${year}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.status === 401) {
          toastRef.current.showToast(
            "Unauthorised access. Please login again.",
            "error"
          );
          await logout();
          return;
        }

        const handleAuthError = handleAuthExpiry(response);
        if (handleAuthError === true) {
          toastRef.current.showToast("Your Session has expired", "error");
          logout();
          return false;
        }

        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }
        const data = await response.json();
        setMonthlyBill(data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    // Make sure `selectedMonth` is available here.
    if (selectedYear) {
      fetchMonthlyBillData(selectedYear);
    }
  }, [token, selectedYear]);
  useEffect(() => {
  }, [monthlyBill]);
  useEffect(() => {
    if (!token || !selectedYear) return;

    const fetchCustomerCountData = async (year) => {
      setLoading(true);
      try {
        const response = await fetch(
          `${reportApiUrl}/GetCustCountByDepartment?year=${year}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 401) {
          toastRef.current.showToast(
            "Unauthorised access. Please login again.",
            "error"
          );
          await logout();
          return;
        }

        const handleAuthError = handleAuthExpiry(response);
        if (handleAuthError === true) {
          toastRef.current.showToast("Your Session has expired", "error");
          logout();
          return false;
        }

        if (!response.ok)
          throw new Error("Failed to fetch customer count data");

        const data = await response.json();
        setCustomerCount(data);
      } catch (error) {
        console.error("Error fetching customer count data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerCountData(selectedYear);
  }, [token, selectedYear]);
  // ✅ Utility: generate empty months object
  const createEmptyMonthlyData = () => ({
    January: 0,
    February: 0,
    March: 0,
    April: 0,
    May: 0,
    June: 0,
    July: 0,
    August: 0,
    September: 0,
    October: 0,
    November: 0,
    December: 0,
  });

  // ✅ Utility: normalize department names (trim + lowercase)
  // If you later decide to strip numbers, just add: .replace(/^\d+\s*/, "")
  const normalizeDept = (dept) => dept.trim().toLowerCase();

  const customerCountResult = Object.keys(
    customerCount?.Departments?.Departments || {}
  ).map((departmentName) => {
    const department = customerCount.Departments.Departments[departmentName];
    const departmentHead = department.DepartmentHead;

    // start with empty monthly counts
    const totalMonthlyCount = createEmptyMonthlyData();

    // sum customer counts across cost centers
    Object.values(department.CostCenters || {}).forEach((costCenter) => {
      totalMonthlyCount.January += costCenter.JanuaryCustomerCount || 0;
      totalMonthlyCount.February += costCenter.FebruaryCustomerCount || 0;
      totalMonthlyCount.March += costCenter.MarchCustomerCount || 0;
      totalMonthlyCount.April += costCenter.AprilCustomerCount || 0;
      totalMonthlyCount.May += costCenter.MayCustomerCount || 0;
      totalMonthlyCount.June += costCenter.JuneCustomerCount || 0;
      totalMonthlyCount.July += costCenter.JulyCustomerCount || 0;
      totalMonthlyCount.August += costCenter.AugustCustomerCount || 0;
      totalMonthlyCount.September += costCenter.SeptemberCustomerCount || 0;
      totalMonthlyCount.October += costCenter.OctoberCustomerCount || 0;
      totalMonthlyCount.November += costCenter.NovemberCustomerCount || 0;
      totalMonthlyCount.December += costCenter.DecemberCustomerCount || 0;
    });

    return {
      departmentName: normalizeDept(departmentName), // ✅ normalized
      departmentHead,
      costCenters: [
        {
          costCenterName: "All Cost Centers",
          monthlyData: totalMonthlyCount,
        },
      ],
    };
  });

  const result = Object.keys(monthlyBill?.Departments?.Departments || {}).map(
    (departmentName) => {
      const department = monthlyBill.Departments.Departments[departmentName];
      const departmentHead = department.DepartmentHead;

      // start with empty monthly data
      const totalMonthlyData = createEmptyMonthlyData();

      // sum monthly data across cost centers
      Object.values(department.CostCenters || {}).forEach((costCenter) => {
        totalMonthlyData.January += costCenter.January || 0;
        totalMonthlyData.February += costCenter.February || 0;
        totalMonthlyData.March += costCenter.March || 0;
        totalMonthlyData.April += costCenter.April || 0;
        totalMonthlyData.May += costCenter.May || 0;
        totalMonthlyData.June += costCenter.June || 0;
        totalMonthlyData.July += costCenter.July || 0;
        totalMonthlyData.August += costCenter.August || 0;
        totalMonthlyData.September += costCenter.September || 0;
        totalMonthlyData.October += costCenter.October || 0;
        totalMonthlyData.November += costCenter.November || 0;
        totalMonthlyData.December += costCenter.December || 0;
      });

      return {
        departmentName: normalizeDept(departmentName), // ✅ normalized
        departmentHead,
        costCenters: [
          {
            costCenterName: "All Cost Centers",
            monthlyData: totalMonthlyData,
          },
        ],
      };
    }
  );

  const [isOpen, setIsOpen] = useState(false); // State to track dropdown open/close
  // Default selected year

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen); // Toggle the dropdown open/close state
  };

  const handleYearChange = (year) => {
    setSelectedYear(year); // Set selected year when user clicks on it
    setIsOpen(false); // Close the dropdown after selecting a year
  };

  const handleDownload = () => {
    const prepareSheetData = (sourceData) => {
      return sourceData.map((department) => {
        const monthlyTotals = department.costCenters.reduce(
          (totals, costCenter) => {
            Object.keys(costCenter.monthlyData).forEach((month) => {
              totals[month] += costCenter.monthlyData[month];
            });
            return totals;
          },
          {
            January: 0,
            February: 0,
            March: 0,
            April: 0,
            May: 0,
            June: 0,
            July: 0,
            August: 0,
            September: 0,
            October: 0,
            November: 0,
            December: 0,
          }
        );

        return {
          "Department Name": department.departmentName,
          "Department Head": department.departmentHead,
          January: monthlyTotals.January.toFixed(2),
          February: monthlyTotals.February.toFixed(2),
          March: monthlyTotals.March.toFixed(2),
          April: monthlyTotals.April.toFixed(2),
          May: monthlyTotals.May.toFixed(2),
          June: monthlyTotals.June.toFixed(2),
          July: monthlyTotals.July.toFixed(2),
          August: monthlyTotals.August.toFixed(2),
          September: monthlyTotals.September.toFixed(2),
          October: monthlyTotals.October.toFixed(2),
          November: monthlyTotals.November.toFixed(2),
          December: monthlyTotals.December.toFixed(2),
        };
      });
    };

    const addGrandTotalRow = (data) => {
      const grandTotals = data.reduce(
        (totals, row) => {
          Object.keys(totals).forEach((key) => {
            if (key !== "Department Name" && key !== "Department Head") {
              totals[key] += parseFloat(row[key]);
            }
          });
          return totals;
        },
        {
          "Department Name": "Total",
          "Department Head": "",
          January: 0,
          February: 0,
          March: 0,
          April: 0,
          May: 0,
          June: 0,
          July: 0,
          August: 0,
          September: 0,
          October: 0,
          November: 0,
          December: 0,
        }
      );

      Object.keys(grandTotals).forEach((key) => {
        if (key !== "Department Name" && key !== "Department Head") {
          grandTotals[key] = grandTotals[key].toFixed(2);
        }
      });

      data.push(grandTotals);
      return data;
    };

    // Prepare both sheets
    const monthlyBillData = addGrandTotalRow(prepareSheetData(result));
    const customerCountData = addGrandTotalRow(
      prepareSheetData(customerCountResult)
    );

    const workbook = XLSX.utils.book_new();

    const worksheet1 = XLSX.utils.json_to_sheet(monthlyBillData);
    XLSX.utils.book_append_sheet(workbook, worksheet1, "Monthly Bill");

    const worksheet2 = XLSX.utils.json_to_sheet(customerCountData);
    XLSX.utils.book_append_sheet(workbook, worksheet2, "Customer Count");

    XLSX.writeFile(workbook, `Combined_Report_${selectedYear}.xlsx`);
  };

  useEffect(() => {
    const tableContainer = document.querySelector(".billing_table");

    if (tableContainer) {
      let isDown = false;
      let startX;
      let scrollLeft;

      const handleMouseDown = (e) => {
        isDown = true;
        tableContainer.classList.add("active");
        startX = e.pageX - tableContainer.offsetLeft;
        scrollLeft = tableContainer.scrollLeft;
      };

      const handleMouseLeave = () => {
        isDown = false;
        tableContainer.classList.remove("active");
      };

      const handleMouseUp = () => {
        isDown = false;
        tableContainer.classList.remove("active");
      };

      const handleMouseMove = (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - tableContainer.offsetLeft;
        const walk = (x - startX) * 2; // Adjust multiplier for speed
        tableContainer.scrollLeft = scrollLeft - walk;
      };

      tableContainer.addEventListener("mousedown", handleMouseDown);
      tableContainer.addEventListener("mouseleave", handleMouseLeave);
      tableContainer.addEventListener("mouseup", handleMouseUp);
      tableContainer.addEventListener("mousemove", handleMouseMove);

      return () => {
        tableContainer.removeEventListener("mousedown", handleMouseDown);
        tableContainer.removeEventListener("mouseleave", handleMouseLeave);
        tableContainer.removeEventListener("mouseup", handleMouseUp);
        tableContainer.removeEventListener("mousemove", handleMouseMove);
      };
    }
  }, []);
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <div>
      <NavSub />

      <div className="nra-top-copfor-col">
        <div className="nra-top-Bill">
          <div className="Nav-btn-user" onClick={handleDownload}>
            <FileArrowUp size={25} color="#3d025f" weight="fill" />
            <button className="Nav-Sub-btn">DOWNLOAD FILES</button>
          </div>

          <div className="synergy-monthly-bill-dd-main">
            <div
              className="synergy-monthly-bill-dd"
              onClick={handleToggleDropdown}
            >
              {`YEAR ${selectedYear}`}
              {isOpen ? <CaretUp weight="fill" /> : <CaretDown weight="fill" />}
            </div>
            {isOpen && (
              <div className="year-dropdown-list-syngergy-monthly-bill">
                {[...Array(11)].map((_, index) => {
                  const year = 2020 + index; // Generate years from 2024 to 2030
                  return (
                    <div
                      key={year}
                      className="year-dropdown-item"
                      onClick={() => handleYearChange(year)}
                    >
                      {year}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          width: "98%",

          margin: "0.5rem auto",

          zIndex: 1,
        }}
        className="billing_table"
      >
        <div className="budget_team_title" style={{ marginBottom: "24px" }}>
          <div className="budget_team_Title_field">
            SYNERGY BILLING BY MONTH
          </div>
        </div>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: "Arial, sans-serif",
            tableLayout: "fixed",
            marginBottom: "40px",
          }}
        >
          <thead
            style={{
              position: "sticky",
              top: 0,
              backgroundColor: "#D9D9D9",
              zIndex: 2,
            }}
          >
            <tr>
              <th
                style={{ width: "250px", padding: "15px", textAlign: "left" }}
              >
                Department
              </th>
              <th
                style={{ width: "300px", padding: "15px", textAlign: "left" }}
              >
                View Group Department Head
              </th>
              {[
                "JAN",
                "FEB",
                "MAR",
                "APR",
                "MAY",
                "JUN",
                "JUL",
                "AUG",
                "SEP",
                "OCT",
                "NOV",
                "DEC",
              ].map((month) => (
                <th
                  key={month}
                  style={{
                    width: "80px",
                    padding: "15px",
                    textAlign: "center",
                  }}
                >
                  {month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.map((department, index) => (
              <tr
                key={department.departmentName}
                style={{
                  backgroundColor: index % 2 === 0 ? "#F1F1F1" : "#FFFFFF",
                }}
              >
                <td style={{ padding: "15px", color: "#898989" }}>
                  {department.departmentName?.charAt(0).toUpperCase() +
                    department.departmentName?.slice(1).toLowerCase()}
                </td>
                <td style={{ padding: "15px", color: "#898989" }}>
                  {department.departmentHead}
                </td>
                {department.costCenters.flatMap((costCenter, idx) => [
                  <td
                    key={`jan-${idx}`}
                    style={{
                      padding: "15px",
                      color: "#898989",
                      textAlign: "center",
                    }}
                  >
                    {costCenter.monthlyData.January?.toFixed(2) || "0.00"}
                  </td>,
                  <td
                    key={`feb-${idx}`}
                    style={{
                      padding: "15px",
                      color: "#898989",
                      textAlign: "center",
                    }}
                  >
                    {costCenter.monthlyData.February?.toFixed(2) || "0.00"}
                  </td>,
                  <td
                    key={`mar-${idx}`}
                    style={{
                      padding: "15px",
                      color: "#898989",
                      textAlign: "center",
                    }}
                  >
                    {costCenter.monthlyData.March?.toFixed(2) || "0.00"}
                  </td>,
                  <td
                    key={`apr-${idx}`}
                    style={{
                      padding: "15px",
                      color: "#898989",
                      textAlign: "center",
                    }}
                  >
                    {costCenter.monthlyData.April?.toFixed(2) || "0.00"}
                  </td>,
                  <td
                    key={`may-${idx}`}
                    style={{
                      padding: "15px",
                      color: "#898989",
                      textAlign: "center",
                    }}
                  >
                    {costCenter.monthlyData.May?.toFixed(2) || "0.00"}
                  </td>,
                  <td
                    key={`jun-${idx}`}
                    style={{
                      padding: "15px",
                      color: "#898989",
                      textAlign: "center",
                    }}
                  >
                    {costCenter.monthlyData.June?.toFixed(2) || "0.00"}
                  </td>,
                  <td
                    key={`jul-${idx}`}
                    style={{
                      padding: "15px",
                      color: "#898989",
                      textAlign: "center",
                    }}
                  >
                    {costCenter.monthlyData.July?.toFixed(2) || "0.00"}
                  </td>,
                  <td
                    key={`aug-${idx}`}
                    style={{
                      padding: "15px",
                      color: "#898989",
                      textAlign: "center",
                    }}
                  >
                    {costCenter.monthlyData.August?.toFixed(2) || "0.00"}
                  </td>,
                  <td
                    key={`sep-${idx}`}
                    style={{
                      padding: "15px",
                      color: "#898989",
                      textAlign: "center",
                    }}
                  >
                    {costCenter.monthlyData.September?.toFixed(2) || "0.00"}
                  </td>,
                  <td
                    key={`oct-${idx}`}
                    style={{
                      padding: "15px",
                      color: "#898989",
                      textAlign: "center",
                    }}
                  >
                    {costCenter.monthlyData.October?.toFixed(2) || "0.00"}
                  </td>,
                  <td
                    key={`nov-${idx}`}
                    style={{
                      padding: "15px",
                      color: "#898989",
                      textAlign: "center",
                    }}
                  >
                    {costCenter.monthlyData.November?.toFixed(2) || "0.00"}
                  </td>,
                  <td
                    key={`dec-${idx}`}
                    style={{
                      padding: "15px",
                      color: "#898989",
                      textAlign: "center",
                    }}
                  >
                    {costCenter.monthlyData.December?.toFixed(2) || "0.00"}
                  </td>,
                ])}
              </tr>
            ))}
            <tr
              style={{
                backgroundColor: "#50145A",
                fontWeight: "bold",
                position: "sticky",
                bottom: 0,
                zIndex: 2,
              }}
            >
              <td colSpan="2" style={{ padding: "15px", color: "#FFFFFF" }}>
                Total Hours
              </td>
              {[
                "January",
                "February",
                "March",
                "April",
                "May",
                "June",
                "July",
                "August",
                "September",
                "October",
                "November",
                "December",
              ].map((month) => {
                const total = result.reduce((sum, department) => {
                  department.costCenters.forEach((costCenter) => {
                    sum += costCenter.monthlyData[month] || 0;
                  });
                  return sum;
                }, 0);
                return (
                  <td
                    key={month}
                    style={{
                      padding: "15px",
                      color: "#FFFFFF",
                      textAlign: "center",
                    }}
                  >
                    {total.toFixed(2)}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>

        {loading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
            }}
          >
            <CircularProgress style={{ color: "purple" }} width={40} />
          </div>
        )}
        {end && <div>No more Data...</div>}
      </div>
      <div
        style={{
          width: "98%",

          margin: "2rem auto",

          zIndex: 1,
        }}
        className="billing_table"
      >
        <div className="budget_team_title" style={{ marginBottom: "24px" }}>
          <div className="budget_team_Title_field">
            SYNERGY BILLING CUSTOMER COUNT BY DEPARMENT
          </div>
        </div>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: "Arial, sans-serif",
            tableLayout: "fixed",
          }}
        >
          <thead
            style={{
              position: "sticky",
              top: 0,
              backgroundColor: "#D9D9D9",
              zIndex: 2,
            }}
          >
            <tr>
              <th
                style={{ width: "250px", padding: "15px", textAlign: "left" }}
              >
                Department
              </th>
              <th
                style={{ width: "300px", padding: "15px", textAlign: "left" }}
              >
                Department Head
              </th>
              {[
                "JAN",
                "FEB",
                "MAR",
                "APR",
                "MAY",
                "JUN",
                "JUL",
                "AUG",
                "SEP",
                "OCT",
                "NOV",
                "DEC",
              ].map((month) => (
                <th
                  key={month}
                  style={{
                    width: "80px",
                    padding: "15px",
                    textAlign: "center",
                  }}
                >
                  {month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {customerCountResult.map((item, index) => (
              <tr
                key={item.departmentName}
                style={{
                  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F1F1F1",
                }}
              >
                <td style={{ padding: "15px", color: "#898989" }}>
                  {item.departmentName?.charAt(0).toUpperCase() +
                    item.departmentName?.slice(1).toLowerCase()}
                </td>
                <td style={{ padding: "15px", color: "#898989" }}>
                  {item.departmentHead}
                </td>
                {Object.entries(item.costCenters[0].monthlyData).map(
                  ([month, value]) => (
                    <td
                      key={month}
                      style={{
                        padding: "15px",
                        color: "#898989",
                        textAlign: "center",
                      }}
                    >
                      {value}
                    </td>
                  )
                )}
              </tr>
            ))}
            <tr
              style={{
                backgroundColor: "#50145A",
                fontWeight: "bold",
                position: "sticky",
                bottom: 0,
                zIndex: 2,
              }}
            >
              <td colSpan="2" style={{ padding: "15px", color: "#FFFFFF" }}>
                Total Customers
              </td>
              {[
                "January",
                "February",
                "March",
                "April",
                "May",
                "June",
                "July",
                "August",
                "September",
                "October",
                "November",
                "December",
              ].map((month) => {
                const total = customerCountResult.reduce((sum, item) => {
                  return sum + (item.costCenters[0].monthlyData[month] || 0);
                }, 0);
                return (
                  <td
                    key={month}
                    style={{
                      padding: "15px",
                      color: "#FFFFFF",
                      textAlign: "center",
                    }}
                  >
                    {total}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SynergyBillMonth;
